/**
 * Batch Details Page
 * Displays batch information and allows QR code download
 */

import { useEffect, useMemo, useState, ChangeEvent } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { productService, ProductBatch, Product } from '../services/product.service';
import { packagingService, PackagingConfig, PackageWithActivation } from '../services/packaging.service';
import { Button, Alert, Modal, PageHeader, StatusBadge, FilterBar, EntityCard } from '../components/ui';
import { enhancedProductService } from '../services/enhanced-product.service';
import {
  printPackService,
  PrintPackLabel,
  PrintPackRequest,
  ThermalLabelRequest,
  ThermalLabelSize
} from '../services/print-pack.service';
import { LabelDesign, ManufacturingDetails } from '../types/enhanced-product-info.types';
import { api } from '../lib/axios';
import { formatBatchName } from '../lib/utils';
import { mapStatusToBadge } from '../lib/status';
import {
  confidentialService,
  ConfidentialRelease,
  ConfidentialSubscriptionStatus,
} from '../services/confidential.service';

export default function BatchDetailsPage() {
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();
  const location = useLocation();
  const MAX_LABELS = 5000; // backend print-pack cap
  
  const [batch, setBatch] = useState<ProductBatch | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [packagingConfig, setPackagingConfig] = useState<PackagingConfig | null>(null);
  const [packages, setPackages] = useState<PackageWithActivation[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [productSort, setProductSort] = useState<'serial-asc' | 'serial-desc' | 'created-desc' | 'created-asc'>(
    'serial-asc'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingUrls, setDownloadingUrls] = useState(false);
  const [downloadingCases, setDownloadingCases] = useState(false);
  const [downloadingPallets, setDownloadingPallets] = useState(false);
  const [creatingPackages, setCreatingPackages] = useState(false);
  const [showPrintPackModal, setShowPrintPackModal] = useState(false);
  const [showThermalLabelModal, setShowThermalLabelModal] = useState(false);
  const [generatingPack, setGeneratingPack] = useState(false);
  const [printPackError, setPrintPackError] = useState<string | null>(null);
  const [printPackSuccess, setPrintPackSuccess] = useState<string | null>(null);
  const [printPackJobId, setPrintPackJobId] = useState<string | null>(null);
  const [printPackJobStatus, setPrintPackJobStatus] = useState<string | null>(null);
  const [printPackProgress, setPrintPackProgress] = useState<number>(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [printOptions, setPrintOptions] = useState({
    pageSize: 'A4' as 'A4' | 'Letter',
    layout: 'sheet' as 'sheet' | 'roll',
    labelWidth: 50,
    labelHeight: 50,
    dpi: 300 as 203 | 300 | 600,
    printerType: 'NONE' as 'ZPL' | 'TSPL' | 'SBPL' | 'NONE',
    includePreviewSvg: true
  });
  const [thermalOptions, setThermalOptions] = useState({
    size: '2x1' as ThermalLabelSize,
    dpi: 203 as 203 | 300 | 600,
    includePdf: true,
    serialNumber: undefined as number | undefined,
    fullBatch: false,
    includeInner: false,
    marginMm: 2.5,
  });
  const [labelDesign, setLabelDesign] = useState<LabelDesign>({
    qrSizeInches: 0.75,
    fontFamily: 'space_grotesk',
    brandFontSize: 10,
    taglineFontSize: 8,
    textFontSize: 9,
    qrLabelFontSize: 10,
    lineHeight: 1.25,
    thermalLabelSize: '2x1',
    layoutPreset: 'qr_left_text_right',
    textAlign: 'center',
    contentGapMm: 0.5,
    qrZoneRatio: 0.5,
    qrTextLayout: 'stacked',
    qrTextOrientation: 'top_to_bottom',
    qrDarkness: 100,
    textDarkness: 100,
    textBold: false,
    qrTextBold: false,
    brandName: '',
    tagline: '',
    website: '',
    qrCaption: 'Scan QR using mobile camera',
    qrBranding: 'powered by pureaum.com',
    freeLines: ['', '', ''],
    logoUrl: '',
    logoPath: '',
    logoWidthMm: 10,
    logoHeightMm: 10
  });
  const [manufacturingDetails, setManufacturingDetails] = useState<ManufacturingDetails | null>(null);
  const [labelDesignSaving, setLabelDesignSaving] = useState(false);
  const [labelDesignError, setLabelDesignError] = useState<string | null>(null);
  const [labelDesignSuccess, setLabelDesignSuccess] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);

  // Package activation state
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activatingPackages, setActivatingPackages] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [confidentialSubscription, setConfidentialSubscription] = useState<ConfidentialSubscriptionStatus | null>(null);
  const [confidentialReleases, setConfidentialReleases] = useState<ConfidentialRelease[]>([]);
  const [confidentialLatest, setConfidentialLatest] = useState<ConfidentialRelease | null>(null);
  const [confidentialLoading, setConfidentialLoading] = useState(false);
  const [confidentialSaving, setConfidentialSaving] = useState(false);
  const [confidentialRevokingId, setConfidentialRevokingId] = useState<string | null>(null);
  const [confidentialError, setConfidentialError] = useState<string | null>(null);
  const [confidentialForm, setConfidentialForm] = useState({
    retailerDiscount: '',
    retailerPromoCode: '',
    retailerInstruction: '',
    dealerDiscount: '',
    dealerPromoCode: '',
    dealerInstruction: '',
    validUntil: '',
    notes: '',
  });

  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
    }
    loadBatchDetails();
  }, [batchId]);

  const thermalLabelSizes: ThermalLabelSize[] = ['2x1', '2x1.5', '2x2', '50x30mm', '50x34mm'];
  const isThermalLabelSize = (value: unknown): value is ThermalLabelSize =>
    typeof value === 'string' && thermalLabelSizes.includes(value as ThermalLabelSize);
  const thermalLabelSizeDimensions: Record<ThermalLabelSize, { widthMm: number; heightMm: number }> = {
    '2x1': { widthMm: 50.8, heightMm: 25.4 },
    '2x1.5': { widthMm: 50.8, heightMm: 38.1 },
    '2x2': { widthMm: 50.8, heightMm: 50.8 },
    '50x30mm': { widthMm: 50, heightMm: 30 },
    '50x34mm': { widthMm: 50, heightMm: 34 }
  };
  const getThermalLabelDimensions = (size: ThermalLabelSize) =>
    thermalLabelSizeDimensions[size] || thermalLabelSizeDimensions['50x30mm'];
  const applyLabelSize = (size: ThermalLabelSize) => {
    const dimensions = getThermalLabelDimensions(size);
    setThermalOptions(prev => ({ ...prev, size }));
    setLabelDesign(prev => ({ ...prev, thermalLabelSize: size }));
    setPrintOptions(prev => ({
      ...prev,
      labelWidth: dimensions.widthMm,
      labelHeight: dimensions.heightMm
    }));
  };

  const normalizeLabelDesign = (design?: LabelDesign | null): LabelDesign => {
    const freeLines = (design?.freeLines || []).slice(0, 3);
    while (freeLines.length < 3) {
      freeLines.push('');
    }

    const clampValue = (value: number | undefined, min: number, max: number, fallback: number) =>
      typeof value === 'number' ? Math.min(max, Math.max(min, value)) : fallback;

    const contentGapMm =
      typeof design?.contentGapMm === 'number'
        ? Math.min(6, Math.max(0.5, design.contentGapMm))
        : 0.5;
    const qrZoneRatio =
      typeof design?.qrZoneRatio === 'number'
        ? Math.min(0.6, Math.max(0.5, design.qrZoneRatio))
        : 0.5;
    const qrTextLayout = design?.qrTextLayout === 'side' ? 'side' : 'stacked';
    const qrTextOrientation =
      design?.qrTextOrientation === 'bottom_to_top' ? 'bottom_to_top' : 'top_to_bottom';
    const qrDarkness =
      typeof design?.qrDarkness === 'number'
        ? Math.min(100, Math.max(40, design.qrDarkness))
        : 100;
    const thermalLabelSize = isThermalLabelSize(design?.thermalLabelSize)
      ? design?.thermalLabelSize
      : '2x1';
    const labelDimensions = getThermalLabelDimensions(thermalLabelSize);
    const textZoneRatio = 1 - qrZoneRatio;
    const maxLogoWidthMm = Math.max(6, (labelDimensions.widthMm * textZoneRatio) / 2);
    const maxLogoHeightMm = Math.max(6, labelDimensions.heightMm / 2);
    const legacyLogoSizeMm = typeof design?.logoSizeMm === 'number' ? design.logoSizeMm : undefined;
    const logoWidthMm = clampValue(
      typeof design?.logoWidthMm === 'number' ? design.logoWidthMm : legacyLogoSizeMm,
      6,
      maxLogoWidthMm,
      Math.min(10, maxLogoWidthMm)
    );
    const logoHeightMm = clampValue(
      typeof design?.logoHeightMm === 'number' ? design.logoHeightMm : legacyLogoSizeMm,
      6,
      maxLogoHeightMm,
      Math.min(10, maxLogoHeightMm)
    );

    const brandFontSize = clampValue(design?.brandFontSize, 8, 22, 10);
    const taglineFontSize = clampValue(design?.taglineFontSize, 4, 22, 8);
    const textFontSize = clampValue(design?.textFontSize, 4, 22, 9);
    const qrLabelFontSize = clampValue(
      typeof design?.qrLabelFontSize === 'number' ? design.qrLabelFontSize : Math.max(10, textFontSize),
      4,
      22,
      Math.max(10, Math.round(textFontSize))
    );
    const textDarkness = clampValue(design?.textDarkness, 40, 100, 100);
    const textBold = Boolean(design?.textBold);
    const qrTextBold = Boolean(design?.qrTextBold);

    return {
      qrSizeInches: typeof design?.qrSizeInches === 'number' ? design.qrSizeInches : 0.75,
      fontFamily: design?.fontFamily || 'space_grotesk',
      brandFontSize,
      taglineFontSize,
      textFontSize,
      qrLabelFontSize,
      lineHeight: clampValue(design?.lineHeight, 0.9, 4.0, 1.25),
      thermalLabelSize,
      layoutPreset: design?.layoutPreset || 'qr_left_text_right',
      textAlign: design?.textAlign || 'center',
      contentGapMm,
      qrZoneRatio,
      qrTextLayout,
      qrTextOrientation,
      qrDarkness,
      textDarkness,
      textBold,
      qrTextBold,
      brandName: design?.brandName || '',
      tagline: design?.tagline || '',
      website: design?.website || '',
      qrCaption: typeof design?.qrCaption === 'string' ? design.qrCaption : 'Scan QR using mobile camera',
      qrBranding: typeof design?.qrBranding === 'string' ? design.qrBranding : 'powered by pureaum.com',
      freeLines,
      logoUrl: design?.logoUrl || '',
      logoPath: design?.logoPath || '',
      logoWidthMm,
      logoHeightMm
    };
  };

  const buildLabelDesignPayload = (design: LabelDesign): LabelDesign => {
    const payload: LabelDesign = {
      qrSizeInches: typeof design.qrSizeInches === 'number' ? design.qrSizeInches : 0.75,
      fontFamily: design.fontFamily || 'space_grotesk',
      layoutPreset: design.layoutPreset || 'qr_left_text_right',
      textAlign: design.textAlign || 'center'
    };
    const clampValue = (value: number, min: number, max: number) =>
      Math.min(max, Math.max(min, value));
    if (typeof design.contentGapMm === 'number') {
      payload.contentGapMm = Math.min(6, Math.max(0.5, design.contentGapMm));
    }
    const qrZoneRatioValue =
      typeof design.qrZoneRatio === 'number' ? clampValue(design.qrZoneRatio, 0.5, 0.6) : 0.5;
    if (typeof design.qrZoneRatio === 'number') {
      payload.qrZoneRatio = qrZoneRatioValue;
    }
    if (design.qrTextLayout === 'side') {
      payload.qrTextLayout = 'side';
    }
    if (design.qrTextOrientation === 'bottom_to_top') {
      payload.qrTextOrientation = 'bottom_to_top';
    }
    if (typeof design.qrDarkness === 'number') {
      payload.qrDarkness = clampValue(design.qrDarkness, 40, 100);
    }
    if (typeof design.brandFontSize === 'number') {
      payload.brandFontSize = clampValue(design.brandFontSize, 8, 22);
    }
    if (typeof design.taglineFontSize === 'number') {
      payload.taglineFontSize = clampValue(design.taglineFontSize, 4, 22);
    }
    if (typeof design.textFontSize === 'number') {
      payload.textFontSize = clampValue(design.textFontSize, 4, 22);
    }
    if (typeof design.qrLabelFontSize === 'number') {
      payload.qrLabelFontSize = clampValue(design.qrLabelFontSize, 4, 22);
    } else if (typeof design.textFontSize === 'number') {
      payload.qrLabelFontSize = clampValue(Math.max(10, design.textFontSize), 4, 22);
    } else {
      payload.qrLabelFontSize = 10;
    }
    if (typeof design.lineHeight === 'number') {
      payload.lineHeight = clampValue(design.lineHeight, 0.9, 4.0);
    }
    if (typeof design.textDarkness === 'number') {
      payload.textDarkness = clampValue(design.textDarkness, 40, 100);
    } else {
      payload.textDarkness = 100;
    }
    payload.textBold = Boolean(design.textBold);
    payload.qrTextBold = Boolean(design.qrTextBold);
    if (isThermalLabelSize(design.thermalLabelSize)) {
      payload.thermalLabelSize = design.thermalLabelSize;
    }

    const brandName = design.brandName?.trim();
    if (brandName) payload.brandName = brandName;
    const tagline = design.tagline?.trim();
    if (tagline) payload.tagline = tagline;
    const website = design.website?.trim();
    if (website) payload.website = website;
    const qrCaption = design.qrCaption?.trim();
    if (qrCaption) payload.qrCaption = qrCaption;
    const qrBranding = design.qrBranding?.trim();
    if (qrBranding) payload.qrBranding = qrBranding;
    const logoUrl = design.logoUrl?.trim();
    if (logoUrl) payload.logoUrl = logoUrl;
    const logoPath = design.logoPath?.trim();
    if (logoPath) payload.logoPath = logoPath;
    const logoClampSize = isThermalLabelSize(design.thermalLabelSize)
      ? design.thermalLabelSize
      : thermalOptions.size;
    const logoClampDimensions = getThermalLabelDimensions(logoClampSize);
    const textZoneRatio = 1 - qrZoneRatioValue;
    const maxLogoWidthMm = Math.max(6, (logoClampDimensions.widthMm * textZoneRatio) / 2);
    const maxLogoHeightMm = Math.max(6, logoClampDimensions.heightMm / 2);
    if (typeof design.logoWidthMm === 'number') {
      payload.logoWidthMm = clampValue(design.logoWidthMm, 6, maxLogoWidthMm);
    }
    if (typeof design.logoHeightMm === 'number') {
      payload.logoHeightMm = clampValue(design.logoHeightMm, 6, maxLogoHeightMm);
    }

    const freeLines = (design.freeLines || []).map(line => line.trim()).filter(Boolean).slice(0, 3);
    if (freeLines.length > 0) payload.freeLines = freeLines;

    return payload;
  };

  const loadConfidentialData = async (targetBatchId: string) => {
    try {
      setConfidentialLoading(true);
      setConfidentialError(null);
      const response = await confidentialService.getBatchReleases(targetBatchId);
      setConfidentialSubscription(response.subscription || null);
      setConfidentialLatest(response.latest || null);
      setConfidentialReleases(response.releases || []);
    } catch (err: any) {
      setConfidentialError(err.response?.data?.error || 'Failed to load confidential release data');
      setConfidentialSubscription(null);
      setConfidentialLatest(null);
      setConfidentialReleases([]);
    } finally {
      setConfidentialLoading(false);
    }
  };

  const loadBatchDetails = async () => {
    if (!batchId) return;

    try {
      setLoading(true);
      setError(null);
      
      const [batchData, productsData] = await Promise.all([
        productService.getBatch(batchId),
        productService.getProductsByBatch(batchId, 10)
      ]);
      
      setBatch(batchData);
      setProducts(productsData);
      const normalizedLabelDesign = normalizeLabelDesign((batchData as any)?.labelDesign);
      const initialLabelSize = normalizedLabelDesign.thermalLabelSize || '2x1';
      const initialDimensions = getThermalLabelDimensions(initialLabelSize);
      setLabelDesign(normalizedLabelDesign);
      setThermalOptions(prev => ({
        ...prev,
        size: initialLabelSize
      }));
      setPrintOptions(prev => ({
        ...prev,
        labelWidth: initialDimensions.widthMm,
        labelHeight: initialDimensions.heightMm
      }));
      setLabelDesignError(null);
      setLabelDesignSuccess(null);
      setLogoUploadError(null);
      if (productsData.length > 0) {
        try {
          const enhancedInfo = await enhancedProductService.getEnhancedProductInfo(productsData[0].id);
          setManufacturingDetails(enhancedInfo?.manufacturingDetails ?? null);
        } catch (err) {
          setManufacturingDetails(null);
        }
      } else {
        setManufacturingDetails(null);
      }

      // Try to load packaging config and packages
      try {
        const configResult = await packagingService.getConfig(batchId);
        if (configResult.success && configResult.data) {
          setPackagingConfig(configResult.data);
          
          // Load packages with activation status
          const packagesResult = await packagingService.getBatchPackagesWithActivation(batchId);
          if (packagesResult.success && packagesResult.data) {
            setPackages(packagesResult.data);
          }
        }
      } catch (err) {
        // Config might not exist, which is fine
      }

      await loadConfidentialData(batchId);
    } catch (err: any) {
      console.error('Failed to load batch details:', err);
      setError(err.response?.data?.error || 'Failed to load batch details');
    } finally {
      setLoading(false);
    }
  };

  const loadAllProductsForPrintPack = async () => {
    if (!batchId) return [];
    try {
      // fetch up to backend cap (5k) to avoid server rejection
      const fullList = await productService.getProductsByBatch(batchId, MAX_LABELS);
      return fullList;
    } catch (err: any) {
      console.error('Failed to load products for print pack:', err);
      throw new Error(err?.message || 'Failed to load products for print pack');
    }
  };

  const handleCreateConfidentialRelease = async () => {
    if (!batchId) return;

    const retailer = {
      discount: confidentialForm.retailerDiscount.trim() || undefined,
      promoCode: confidentialForm.retailerPromoCode.trim() || undefined,
      instruction: confidentialForm.retailerInstruction.trim() || undefined,
    };

    if (!retailer.discount && !retailer.promoCode && !retailer.instruction) {
      setConfidentialError('At least one retailer confidential field is required.');
      return;
    }

    const dealer = {
      discount: confidentialForm.dealerDiscount.trim() || undefined,
      promoCode: confidentialForm.dealerPromoCode.trim() || undefined,
      instruction: confidentialForm.dealerInstruction.trim() || undefined,
    };

    try {
      setConfidentialSaving(true);
      setConfidentialError(null);
      await confidentialService.createRelease(batchId, {
        retailer,
        dealer: dealer.discount || dealer.promoCode || dealer.instruction ? dealer : undefined,
        notes: confidentialForm.notes.trim() || undefined,
        validUntil: confidentialForm.validUntil || undefined,
      });
      setSuccess('Confidential release published.');
      setConfidentialForm((current) => ({
        ...current,
        dealerDiscount: '',
        dealerPromoCode: '',
        dealerInstruction: '',
        notes: '',
      }));
      await loadConfidentialData(batchId);
    } catch (err: any) {
      setConfidentialError(err.response?.data?.error || 'Failed to publish confidential release');
    } finally {
      setConfidentialSaving(false);
    }
  };

  const handleRevokeConfidentialRelease = async (releaseId: string) => {
    if (!batchId) return;
    if (!confirm('Revoke this confidential release?')) return;

    try {
      setConfidentialRevokingId(releaseId);
      setConfidentialError(null);
      await confidentialService.revokeRelease(batchId, releaseId);
      setSuccess('Confidential release revoked.');
      await loadConfidentialData(batchId);
    } catch (err: any) {
      setConfidentialError(err.response?.data?.error || 'Failed to revoke confidential release');
    } finally {
      setConfidentialRevokingId(null);
    }
  };

  const openConfidentialSection = () => {
    if (typeof document === 'undefined') {
      return;
    }
    const section = document.getElementById('confidential-info-section');
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const innerEnabled = batch?.innerQrEnabled ?? true;

  const buildPrintLabels = (prodList: Product[]) => {
    const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
    const producerName =
      (batch as any)?.manufacturerName ||
      (batch as any)?.manufacturer?.name ||
      undefined;
    return prodList.flatMap(p => {
      const common = {
        sku: batch?.productName,
        batchId: batch?.id,
        serial: p.serialNumber,
        producerName,
      };
      const labels: PrintPackLabel[] = [
        {
          ...common,
          verifyUrl: `${baseUrl}/vf/${p.outerQrToken}`,
          variant: 'outer',
        },
      ];
      if (innerEnabled && p.innerQrToken) {
        labels.push({
          ...common,
          verifyUrl: `${baseUrl}/vf/${p.innerQrToken}`,
          variant: 'inner',
        });
      }
      return labels;
    });
  };

  const handleFreeLineChange = (index: number, value: string) => {
    setLabelDesign(prev => {
      const freeLines = [...(prev.freeLines || [])];
      while (freeLines.length < 3) {
        freeLines.push('');
      }
      freeLines[index] = value;
      return { ...prev, freeLines };
    });
  };

  const handleSaveLabelDesign = async () => {
    if (!batchId) return;
    try {
      setLabelDesignSaving(true);
      setLabelDesignError(null);
      setLabelDesignSuccess(null);

      const payload = buildLabelDesignPayload({
        ...labelDesign,
        thermalLabelSize: thermalOptions.size
      });
      await enhancedProductService.updateProductInformation(batchId, 'label_design', { labelDesign: payload });
      setLabelDesign(normalizeLabelDesign(payload));
      setBatch(prev => (prev ? { ...prev, labelDesign: payload } : prev));
      setLabelDesignSuccess('Label design saved.');
    } catch (err: any) {
      setLabelDesignError(err.response?.data?.message || err.response?.data?.error || 'Failed to save label design');
    } finally {
      setLabelDesignSaving(false);
    }
  };

  const handleLogoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !batchId) return;

    try {
      setLogoUploading(true);
      setLogoUploadError(null);

      const uploadResult = await enhancedProductService.uploadLabelLogo(batchId, file);
      setLabelDesign(prev => ({
        ...prev,
        logoUrl: uploadResult.logoUrl,
        logoPath: uploadResult.logoPath
      }));
      setLabelDesignSuccess('Logo uploaded. Save label design to apply.');
    } catch (err: any) {
      setLogoUploadError(err.response?.data?.message || err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleClearLogo = () => {
    setLabelDesign(prev => ({
      ...prev,
      logoUrl: '',
      logoPath: ''
    }));
  };

  const formatMrpLine = (value?: number | string) => {
    if (value === undefined || value === null) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      const formatted = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(numeric);
      return `MRP INR ${formatted}/-`;
    }
    const upper = raw.toUpperCase();
    return upper.startsWith('MRP') ? raw : `MRP ${raw}`;
  };

  const previewWidthPx = 240;
  const previewRatioMap: Record<'2x1' | '2x1.5' | '2x2' | '50x30mm' | '50x34mm', number> = {
    '2x1': 2 / 1,
    '2x1.5': 2 / 1.5,
    '2x2': 1,
    '50x30mm': 50 / 30,
    '50x34mm': 50 / 34
  };
  const previewRatio = previewRatioMap[thermalOptions.size];
  const previewHeightPx = Math.round(previewWidthPx / previewRatio);
  const previewLabelDimensions = getThermalLabelDimensions(thermalOptions.size);
  const previewMarginMm =
    typeof thermalOptions.marginMm === 'number' ? Math.min(6, Math.max(0.5, thermalOptions.marginMm)) : 2.5;
  const previewPaddingPx = Math.max(
    4,
    Math.round((previewMarginMm / previewLabelDimensions.widthMm) * previewWidthPx)
  );
  const previewLayout = labelDesign.layoutPreset || 'qr_left_text_right';
  const previewIsVertical = previewLayout === 'qr_top_text_bottom';
  const previewQrOnLeft =
    previewLayout === 'qr_left_text_right' || previewLayout === 'qr_left_logo_text_right';
  const previewLayoutClass = previewIsVertical
    ? 'flex-col'
    : previewQrOnLeft
    ? 'flex-row'
    : 'flex-row-reverse';
  const previewQrZoneRatio = Math.min(0.6, Math.max(0.5, labelDesign.qrZoneRatio ?? 0.5));
  const previewQrZoneWidthPx = previewIsVertical
    ? previewWidthPx
    : Math.round(previewWidthPx * previewQrZoneRatio);
  const previewTextZoneWidthPx = previewIsVertical ? previewWidthPx : previewWidthPx - previewQrZoneWidthPx;
  const previewZoneHeightPx = previewHeightPx;
  const previewQrZoneInnerWidthPx = Math.max(1, previewQrZoneWidthPx - previewPaddingPx * 2);
  const previewTextZoneInnerWidthPx = Math.max(1, previewTextZoneWidthPx - previewPaddingPx * 2);
  const previewZoneInnerHeightPx = Math.max(1, previewZoneHeightPx - previewPaddingPx * 2);
  const previewQrTextLayout = labelDesign.qrTextLayout === 'side' ? 'side' : 'stacked';
  const previewQrTextOrientation =
    labelDesign.qrTextOrientation === 'bottom_to_top' ? 'bottom_to_top' : 'top_to_bottom';
  const previewQrLabelSize = Math.max(
    4,
    Math.round(labelDesign.qrLabelFontSize ?? Math.max(10, labelDesign.textFontSize ?? 9))
  );
  const previewQrLabelLineHeight = Math.max(1, previewQrLabelSize * (labelDesign.lineHeight ?? 1.25));
  const previewQrDarkness =
    typeof labelDesign.qrDarkness === 'number' ? Math.min(100, Math.max(40, labelDesign.qrDarkness)) : 100;
  const previewQrShade = Math.round(255 * (1 - previewQrDarkness / 100));
  const previewQrFill = `rgb(${previewQrShade}, ${previewQrShade}, ${previewQrShade})`;
  const previewQrTextColor = '#FFFFFF';
  const previewTextDarkness =
    typeof labelDesign.textDarkness === 'number' ? Math.min(100, Math.max(40, labelDesign.textDarkness)) : 100;
  const previewTextShade = Math.round(255 * (1 - previewTextDarkness / 100));
  const previewTextColor = `rgb(${previewTextShade}, ${previewTextShade}, ${previewTextShade})`;
  const previewBodyTextColor = 'rgb(0, 0, 0)';
  const previewQrLabelColor = 'rgb(0, 0, 0)';
  const previewTextBold = Boolean(labelDesign.textBold);
  const previewQrTextBold = Boolean(labelDesign.qrTextBold);
  const previewQrTextGapPx = Math.max(
    1,
    Math.round((0.5 / previewLabelDimensions.widthMm) * previewWidthPx)
  );
  const previewSideGapPx = previewQrTextGapPx;
  const previewSideLineCount =
    previewQrTextLayout === 'side'
      ? [labelDesign.qrBranding, labelDesign.qrCaption].filter(Boolean).length || 0
      : 0;
  const previewSideInterLineGaps =
    previewSideLineCount > 1 ? previewSideGapPx * (previewSideLineCount - 1) : 0;
  const previewSideBlockGapPx =
    previewSideLineCount > 1 && labelDesign.qrBranding && labelDesign.qrCaption ? previewSideGapPx : 0;
  const previewSideColumnWidth =
    previewSideLineCount > 0
      ? Math.round(
          previewSideLineCount * previewQrLabelLineHeight +
            previewSideInterLineGaps +
            previewSideBlockGapPx
        )
      : 0;
  const previewSideTextGapPx = previewSideLineCount > 0 ? previewSideGapPx : 0;
  const previewQrMaxWidthPx =
    previewQrTextLayout === 'side' && !previewIsVertical
      ? Math.max(1, previewQrZoneInnerWidthPx - previewSideColumnWidth - previewSideTextGapPx)
      : previewQrZoneInnerWidthPx;
  const previewQrPx = Math.max(
    32,
    Math.min(
      Math.round(((labelDesign.qrSizeInches ?? 0.75) / 2) * previewWidthPx),
      Math.floor(Math.min(previewQrMaxWidthPx, previewZoneInnerHeightPx))
    )
  );
  const previewGapPx = Math.max(
    2,
    Math.round(((labelDesign.contentGapMm ?? 0.5) / 25.4 / 2) * previewWidthPx)
  );
  const previewTextZoneRatio = 1 - previewQrZoneRatio;
  const previewMaxLogoWidthMm = Math.max(6, (previewLabelDimensions.widthMm * previewTextZoneRatio) / 2);
  const previewMaxLogoHeightMm = previewLabelDimensions.heightMm / 2;
  const previewLogoWidthMm = Math.min(labelDesign.logoWidthMm ?? 10, previewMaxLogoWidthMm);
  const previewLogoHeightMm = Math.min(labelDesign.logoHeightMm ?? 10, previewMaxLogoHeightMm);
  const previewLogoWidthPx = Math.round((previewLogoWidthMm / previewLabelDimensions.widthMm) * previewWidthPx);
  const previewLogoHeightPx = Math.round((previewLogoHeightMm / previewLabelDimensions.heightMm) * previewHeightPx);
  const previewAlignClass =
    labelDesign.textAlign === 'left'
      ? 'items-start text-left'
      : labelDesign.textAlign === 'right'
      ? 'items-end text-right'
      : 'items-center text-center';
  const websitePreview = labelDesign.website
    ? labelDesign.website.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '')
    : '';
  const previewBrandSize = labelDesign.brandFontSize ?? 10;
  const previewTaglineSize = labelDesign.taglineFontSize ?? 8;
  const previewTextSize = labelDesign.textFontSize ?? 9;
  const previewLineHeight = labelDesign.lineHeight ?? 1.25;
  const previewCaption = (labelDesign.qrCaption || 'Scan QR using mobile camera').trim();
  const previewCaptionSize = previewQrLabelSize;
  const previewBranding = (labelDesign.qrBranding || 'powered by pureaum.com').trim();
  const previewBrandingSize = previewQrLabelSize;
  const previewMrpLine = formatMrpLine(manufacturingDetails?.mrp);
  const rawPreviewLines = (labelDesign.freeLines || []).map(line => line.trim()).filter(Boolean).slice(0, 3);
  const previewLine1 = rawPreviewLines[0] || '';
  const previewLine2 = rawPreviewLines[1] || '';
  const previewLine3 = previewMrpLine || rawPreviewLines[2] || '';
  const trailingPreviewLines = [previewLine2, previewLine3].filter(Boolean);
  const previewBodyWeight = previewTextBold ? 600 : undefined;
  const previewBrandWeight = previewTextBold ? 700 : 600;
  const previewLineItems = [
    labelDesign.brandName?.trim()
      ? {
          text: labelDesign.brandName.trim(),
          size: previewBrandSize,
          weight: previewBrandWeight,
          color: previewTextColor
        }
      : null,
    labelDesign.tagline?.trim()
      ? {
          text: labelDesign.tagline.trim(),
          size: previewTaglineSize,
          weight: previewBodyWeight,
          color: previewBodyTextColor
        }
      : null,
    websitePreview
      ? { text: websitePreview, size: previewTextSize, weight: previewBodyWeight, color: previewBodyTextColor }
      : null,
    previewLine1
      ? { text: previewLine1, size: previewTextSize, weight: previewBodyWeight, color: previewBodyTextColor }
      : null,
    ...trailingPreviewLines.map(line => ({
      text: line,
      size: previewTextSize,
      weight: previewBodyWeight,
      color: previewBodyTextColor
    }))
  ].filter(Boolean) as Array<{ text: string; size: number; weight?: number; color?: string }>;
  const previewTextLines =
    previewLineItems.length > 0
      ? previewLineItems
      : [
          { text: 'Brand Name', size: previewBrandSize, weight: previewBrandWeight, color: previewTextColor },
          { text: 'Tagline', size: previewTaglineSize, weight: previewBodyWeight, color: previewBodyTextColor },
          { text: 'yourwebsite.com', size: previewTextSize, weight: previewBodyWeight, color: previewBodyTextColor }
        ];
  const hasLogoAsset = Boolean(labelDesign.logoUrl || labelDesign.logoPath);
  const showLogoPreview = hasLogoAsset;
  const previewTextJustifyClass = hasLogoAsset ? 'justify-start' : 'justify-center';
  const previewTextGapClass = hasLogoAsset ? 'mt-2' : '';

  const pollJobStatus = async (jobId: string) => {
    try {
      const status = await printPackService.getJobStatus(jobId);
      setPrintPackJobStatus(status.status);
      setPrintPackProgress(status.progress || 0);
      if (status.status === 'completed' && status.download) {
        const batchLabel = batch?.productName || batchId || 'batch';
        if (status.download.pdf) await downloadJobFile(status.download.pdf, `${batchLabel}-labels.pdf`);
        if (status.download.zip) await downloadJobFile(status.download.zip, `${batchLabel}-printer-pack.zip`);
        setPrintPackSuccess('Print pack generated. Downloads started.');
        setGeneratingPack(false);
      } else if (status.status === 'failed') {
        setPrintPackError(status.error || 'Print pack job failed');
        setGeneratingPack(false);
      } else {
        setTimeout(() => pollJobStatus(jobId), 4000);
      }
    } catch (err: any) {
      setPrintPackError(err?.message || 'Failed to fetch job status');
      setGeneratingPack(false);
    }
  };

  const handleGenerateThermalLabel = async () => {
    if (!batchId || !batch) return;
    try {
      setGeneratingPack(true);
      setPrintPackError(null);
      setPrintPackSuccess(null);

      const allProducts = await loadAllProductsForPrintPack();
      if (!allProducts.length) {
        throw new Error('No products available for label generation');
      }

      const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
      const producerName =
        (batch as any)?.manufacturerName ||
        (batch as any)?.manufacturer?.name;
      const mrpValue = manufacturingDetails?.mrp;
      const mrpPayload =
        mrpValue !== undefined && mrpValue !== null ? { mrp: mrpValue } : {};

      const marginMm =
        typeof thermalOptions.marginMm === 'number' ? Math.min(6, Math.max(0.5, thermalOptions.marginMm)) : undefined;
      const payload: ThermalLabelRequest = {
        size: thermalOptions.size,
        dpi: thermalOptions.dpi,
        includePdf: thermalOptions.includePdf,
        marginMm,
        labelDesign: buildLabelDesignPayload(labelDesign),
        producerName,
        title: 'Powered by pureaum.com',
        subtitle: 'Pureaum Verified',
        labels: [],
      };
      const includeInner = innerEnabled && thermalOptions.includeInner;

      if (thermalOptions.fullBatch) {
        payload.labels = allProducts.flatMap(p => {
          const outer = {
            qrData: `${baseUrl}/vf/${p.outerQrToken}`,
            title: 'Powered by pureaum.com',
            subtitle: 'Pureaum Verified',
            producerName,
            labelType: 'OUTER QR',
            ...mrpPayload,
          };
          const inner = includeInner && p.innerQrToken
            ? {
                qrData: `${baseUrl}/vf/${p.innerQrToken}`,
                title: 'Powered by pureaum.com',
                subtitle: 'Pureaum Verified',
                producerName,
                labelType: 'INNER QR',
                ...mrpPayload,
              }
            : null;
          return inner ? [outer, inner] : [outer];
        });
      } else {
        const serial =
          thermalOptions.serialNumber !== undefined
            ? thermalOptions.serialNumber
            : allProducts[0]?.serialNumber;
        const product =
          allProducts.find(p => p.serialNumber === serial) || allProducts[0];
        if (includeInner && product?.innerQrToken) {
          payload.labels = [
            {
              qrData: `${baseUrl}/vf/${product.outerQrToken}`,
              title: 'Powered by pureaum.com',
              subtitle: 'Pureaum Verified',
              producerName,
              labelType: 'OUTER QR',
              ...mrpPayload,
            },
            {
              qrData: `${baseUrl}/vf/${product.innerQrToken}`,
              title: 'Powered by pureaum.com',
              subtitle: 'Pureaum Verified',
              producerName,
              labelType: 'INNER QR',
              ...mrpPayload,
            },
          ];
          payload.qrData = undefined;
          payload.labelType = undefined;
        } else {
          payload.qrData = `${baseUrl}/vf/${product.outerQrToken}`;
          if (mrpValue !== undefined && mrpValue !== null) {
            payload.mrp = mrpValue;
          }
          payload.labels = undefined;
          payload.labelType = 'OUTER QR';
        }
      }

      const result = await printPackService.generateThermalLabel(payload);
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate thermal label');
      }

      if (result.zipBase64) {
        downloadBase64File(result.zipBase64, `thermal-${batch.id}-batch.zip`, 'application/zip');
      } else if (result.pngBase64) {
        downloadBase64File(result.pngBase64, `thermal-${batch.id}.png`, 'image/png');
      }
      if (result.pdfBase64) {
        downloadBase64File(result.pdfBase64, `thermal-${batch.id}.pdf`, 'application/pdf');
      }

      setPrintPackSuccess(`Thermal label(s) generated. Count: ${result.count}`);
    } catch (err: any) {
      setPrintPackError(err?.message || 'Failed to generate thermal label');
    } finally {
      setGeneratingPack(false);
    }
  };

  const handleGeneratePrintPack = async () => {
    if (!batchId || !batch) return;
    try {
      setGeneratingPack(true);
      setPrintPackError(null);
      setPrintPackSuccess(null);
      setPrintPackJobId(null);
      setPrintPackJobStatus(null);
      setPrintPackProgress(0);

      const allProducts = await loadAllProductsForPrintPack();
      const labels = buildPrintLabels(allProducts).slice(0, MAX_LABELS);
      if (allProducts.length > MAX_LABELS) {
        setPrintPackSuccess(`Note: generated first ${MAX_LABELS} labels (backend cap reached).`);
      }
      const selectedSize = (labelDesign.thermalLabelSize || thermalOptions.size) as ThermalLabelSize;
      const selectedDimensions = getThermalLabelDimensions(selectedSize);
      const payload: PrintPackRequest = {
        jobId: `batch-${batch.id}-${Date.now()}`,
        layout: printOptions.layout,
        pageSize: printOptions.pageSize,
        labelSizeMm: { width: selectedDimensions.widthMm, height: selectedDimensions.heightMm },
        dpi: printOptions.dpi,
        printerType: printOptions.printerType,
        includePreviewSvg: printOptions.includePreviewSvg,
        labels
      };

      const jobStart = await printPackService.startJob(payload);
      if (!jobStart.success || !jobStart.jobId) {
        throw new Error(jobStart.error || 'Failed to start print pack job');
      }
      setPrintPackJobId(jobStart.jobId);
      setPrintPackJobStatus('processing');
      setPrintPackProgress(0.1);
      pollJobStatus(jobStart.jobId);
    } catch (err: any) {
      setPrintPackError(err?.message || 'Failed to generate print pack');
      setGeneratingPack(false);
    }
  };

  const downloadBase64File = (base64: string, filename: string, mime: string) => {
    const link = document.createElement('a');
    link.href = `data:${mime};base64,${base64}`;
    link.download = filename;
    link.click();
  };

  const downloadJobFile = async (relativeUrl: string, filename: string) => {
    try {
      const cleanedPath = relativeUrl.startsWith('/api/')
        ? relativeUrl.replace(/^\/api/, '')
        : relativeUrl;
      const { data } = await api.get(cleanedPath, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setPrintPackError(err?.message || 'Failed to download print pack file');
    }
  };

  const formatInches = (mm: number) => {
    if (!Number.isFinite(mm)) return '--';
    return (mm / 25.4).toFixed(2);
  };

  const formatMm = (inches: number) => {
    if (!Number.isFinite(inches)) return '--';
    return (inches * 25.4).toFixed(1);
  };

  const activeLabelSize = (labelDesign.thermalLabelSize || thermalOptions.size) as ThermalLabelSize;
  const activeLabelDimensions = getThermalLabelDimensions(activeLabelSize);

  const printPackModal = (
    <Modal isOpen={showPrintPackModal} onClose={() => setShowPrintPackModal(false)} title="Print Pack (A4/Letter)">
      <div className="space-y-4">
        {printPackError && <Alert variant="error">{printPackError}</Alert>}
        {printPackSuccess && <Alert variant="success">{printPackSuccess}</Alert>}
        <Alert variant="info">Max 5,000 labels per print-pack job (outer+inner combined).</Alert>
        {printPackJobStatus && (
          <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <span className="font-medium">Job status: {printPackJobStatus}</span>
              {printPackJobId && <span className="text-gray-500">ID: {printPackJobId}</span>}
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded h-2">
              <div
                className="h-2 rounded bg-blue-500"
                style={{ width: `${Math.min(100, Math.round((printPackProgress || 0) * 100))}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex flex-col text-sm text-gray-700">
            Page Size
            <select
              className="mt-1 border rounded px-2 py-1"
              value={printOptions.pageSize}
              onChange={e => setPrintOptions({ ...printOptions, pageSize: e.target.value as 'A4' | 'Letter' })}
            >
              <option value="A4">A4</option>
              <option value="Letter">Letter</option>
            </select>
          </label>
          <label className="flex flex-col text-sm text-gray-700">
            Layout
            <select
              className="mt-1 border rounded px-2 py-1"
              value={printOptions.layout}
              onChange={e => setPrintOptions({ ...printOptions, layout: e.target.value as 'sheet' | 'roll' })}
            >
              <option value="sheet">Sheet</option>
              <option value="roll">Roll</option>
            </select>
          </label>
          <label className="flex flex-col text-sm text-gray-700 sm:col-span-2">
            Label Size
            <select
              className="mt-1 border rounded px-2 py-1"
              value={activeLabelSize}
              onChange={e => applyLabelSize(e.target.value as ThermalLabelSize)}
            >
              <option value="2x1">2.0 x 1.0 in (50.8 x 25.4 mm)</option>
              <option value="2x1.5">2.0 x 1.5 in (50.8 x 38.1 mm)</option>
              <option value="2x2">2.0 x 2.0 in (50.8 x 50.8 mm)</option>
              <option value="50x30mm">50 x 30 mm (1.97 x 1.18 in)</option>
              <option value="50x34mm">50 x 34 mm (1.97 x 1.34 in)</option>
            </select>
            <span className="mt-1 text-xs text-gray-500">
              {activeLabelDimensions.widthMm} x {activeLabelDimensions.heightMm} mm (
              ~ {formatInches(activeLabelDimensions.widthMm)} x {formatInches(activeLabelDimensions.heightMm)} in)
            </span>
          </label>
          <label className="flex flex-col text-sm text-gray-700">
            DPI
            <select
              className="mt-1 border rounded px-2 py-1"
              value={printOptions.dpi}
              onChange={e => setPrintOptions({ ...printOptions, dpi: Number(e.target.value) as 203 | 300 | 600 })}
            >
              <option value={203}>203</option>
              <option value={300}>300</option>
              <option value={600}>600</option>
            </select>
          </label>
          <label className="flex flex-col text-sm text-gray-700">
            Printer Type
            <select
              className="mt-1 border rounded px-2 py-1"
              value={printOptions.printerType}
              onChange={e => setPrintOptions({ ...printOptions, printerType: e.target.value as any })}
            >
              <option value="NONE">None (PDF/ZIP only)</option>
              <option value="ZPL">Zebra (ZPL)</option>
              <option value="TSPL">TSC (TSPL)</option>
              <option value="SBPL">SATO (SBPL)</option>
            </select>
          </label>
          <label className="flex items-center space-x-2 text-sm text-gray-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={printOptions.includePreviewSvg}
              onChange={e => setPrintOptions({ ...printOptions, includePreviewSvg: e.target.checked })}
              aria-label="Include preview SVG in print pack"
            />
            <span>Include preview SVG</span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => setShowPrintPackModal(false)}
            disabled={generatingPack}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button onClick={handleGeneratePrintPack} disabled={generatingPack} className="w-full sm:w-auto">
            {generatingPack ? 'Generating...' : 'Generate & Download'}
          </Button>
        </div>

      </div>
    </Modal>
  );

  const thermalLabelModal = (
    <Modal isOpen={showThermalLabelModal} onClose={() => setShowThermalLabelModal(false)} title="Thermal Labels">
      <div className="space-y-4">
        {printPackError && <Alert variant="error">{printPackError}</Alert>}
        {printPackSuccess && <Alert variant="success">{printPackSuccess}</Alert>}
        <div className="border-t pt-4 mt-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-800">Thermal Label Design</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveLabelDesign}
              disabled={labelDesignSaving}
              className="w-full sm:w-auto"
            >
              {labelDesignSaving ? 'Saving...' : 'Save Design'}
            </Button>
          </div>
          {labelDesignError && <Alert variant="error">{labelDesignError}</Alert>}
          {logoUploadError && <Alert variant="error">{logoUploadError}</Alert>}
          {labelDesignSuccess && <Alert variant="success">{labelDesignSuccess}</Alert>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col text-sm text-gray-700">
              Layout preset
              <select
                className="mt-1 border rounded px-2 py-1"
                value={labelDesign.layoutPreset || 'qr_left_text_right'}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    layoutPreset: e.target.value as LabelDesign['layoutPreset']
                  })
                }
              >
                <option value="qr_left_text_right">QR left / text right</option>
                <option value="qr_right_text_left">QR right / text left</option>
                <option value="qr_top_text_bottom">QR top / text below</option>
                <option value="qr_left_logo_text_right">QR left / logo + text right</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Text alignment
              <select
                className="mt-1 border rounded px-2 py-1"
                value={labelDesign.textAlign || 'center'}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    textAlign: e.target.value as LabelDesign['textAlign']
                  })
                }
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Brand name
              <input
                className="mt-1 border rounded px-2 py-1"
                value={labelDesign.brandName || ''}
                maxLength={80}
                onChange={e => setLabelDesign({ ...labelDesign, brandName: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Tagline
              <input
                className="mt-1 border rounded px-2 py-1"
                value={labelDesign.tagline || ''}
                maxLength={120}
                onChange={e => setLabelDesign({ ...labelDesign, tagline: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Website
              <input
                className="mt-1 border rounded px-2 py-1"
                value={labelDesign.website || ''}
                maxLength={120}
                onChange={e => setLabelDesign({ ...labelDesign, website: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              QR caption
              <input
                className="mt-1 border rounded px-2 py-1"
                value={labelDesign.qrCaption || ''}
                maxLength={80}
                placeholder="Scan QR using mobile camera"
                onChange={e => setLabelDesign({ ...labelDesign, qrCaption: e.target.value })}
              />
              <span className="text-xs text-gray-500 mt-1">
                Small text shown under the QR code (wraps up to 3 lines)
              </span>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              QR branding (top)
              <input
                className="mt-1 border rounded px-2 py-1 bg-gray-100 text-gray-500 cursor-not-allowed"
                value={labelDesign.qrBranding || ''}
                maxLength={80}
                placeholder="powered by pureaum.com"
                readOnly
              />
              <span className="text-xs text-gray-500 mt-1">
                Tiny text shown above the QR code for branding
              </span>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              QR size (in / mm)
              <select
                className="mt-1 border rounded px-2 py-1"
                value={String(labelDesign.qrSizeInches ?? 0.75)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    qrSizeInches: Number(e.target.value)
                  })
                }
              >
                <option value="0.6">0.60 in (15.2 mm)</option>
                <option value="0.7">0.70 in (17.8 mm)</option>
                <option value="0.75">0.75 in (19.1 mm) (recommended)</option>
                <option value="0.8">0.80 in (20.3 mm)</option>
                <option value="0.9">0.90 in (22.9 mm)</option>
              </select>
              <span className="text-xs text-gray-500 mt-1">
                ~ {formatMm(labelDesign.qrSizeInches ?? 0.75)} mm
              </span>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              QR darkness (%)
              <select
                className="mt-1 border rounded px-2 py-1"
                value={String(labelDesign.qrDarkness ?? 100)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    qrDarkness: Number(e.target.value)
                  })
                }
              >
                <option value="100">100 (Black)</option>
                <option value="90">90</option>
                <option value="80">80</option>
                <option value="70">70</option>
                <option value="60">60</option>
                <option value="50">50</option>
                <option value="40">40 (Light)</option>
              </select>
              <span className="text-xs text-gray-500 mt-1">
                Lower values print lighter; too light may affect scanning
              </span>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              QR text layout
              <select
                className="mt-1 border rounded px-2 py-1"
                value={labelDesign.qrTextLayout || 'stacked'}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    qrTextLayout: e.target.value as LabelDesign['qrTextLayout']
                  })
                }
              >
                <option value="stacked">Stacked (top/bottom)</option>
                <option value="side">Side (vertical)</option>
              </select>
            </label>
            {labelDesign.qrTextLayout === 'side' && (
              <label className="flex flex-col text-sm text-gray-700">
                QR text orientation
                <select
                  className="mt-1 border rounded px-2 py-1"
                  value={labelDesign.qrTextOrientation || 'top_to_bottom'}
                  onChange={e =>
                    setLabelDesign({
                      ...labelDesign,
                      qrTextOrientation: e.target.value as LabelDesign['qrTextOrientation']
                    })
                  }
                >
                  <option value="top_to_bottom">Top to bottom</option>
                  <option value="bottom_to_top">Bottom to top</option>
                </select>
              </label>
            )}
            <label className="flex flex-col text-sm text-gray-700">
              QR branding/caption size
              <select
                className="mt-1 border rounded px-2 py-1"
                value={String(labelDesign.qrLabelFontSize ?? Math.max(10, labelDesign.textFontSize ?? 9))}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    qrLabelFontSize: Number(e.target.value)
                  })
                }
              >
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
                <option value="13">13</option>
                <option value="14">14</option>
                <option value="15">15</option>
                <option value="16">16</option>
                <option value="17">17</option>
                <option value="18">18</option>
                <option value="19">19</option>
                <option value="20">20</option>
                <option value="21">21</option>
                <option value="22">22</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              QR text weight
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={labelDesign.qrTextBold ?? false}
                  onChange={e =>
                    setLabelDesign({
                      ...labelDesign,
                      qrTextBold: e.target.checked
                    })
                  }
                  aria-label="Use bold weight for QR text"
                />
                <span>Bold</span>
              </div>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              QR zone width (%)
              <select
                className="mt-1 border rounded px-2 py-1"
                value={String(labelDesign.qrZoneRatio ?? 0.5)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    qrZoneRatio: Number(e.target.value)
                  })
                }
              >
                <option value="0.5">50%</option>
                <option value="0.55">55%</option>
                <option value="0.6">60%</option>
              </select>
              <span className="text-xs text-gray-500 mt-1">
                Applies to left/right layouts only
              </span>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Font
              <select
                className="mt-1 border rounded px-2 py-1"
                value={labelDesign.fontFamily || 'space_grotesk'}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    fontFamily: e.target.value as LabelDesign['fontFamily']
                  })
                }
              >
                <option value="space_grotesk">Space Grotesk (default)</option>
                <option value="roboto_condensed">Condensed (Roboto Condensed)</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Brand font size
              <select
                className="mt-1 border rounded px-2 py-1"
                value={String(labelDesign.brandFontSize ?? 10)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    brandFontSize: Number(e.target.value)
                  })
                }
              >
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
                <option value="13">13</option>
                <option value="14">14</option>
                <option value="15">15</option>
                <option value="16">16</option>
                <option value="17">17</option>
                <option value="18">18</option>
                <option value="19">19</option>
                <option value="20">20</option>
                <option value="21">21</option>
                <option value="22">22</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Tagline font size
              <select
                className="mt-1 border rounded px-2 py-1"
                value={String(labelDesign.taglineFontSize ?? 8)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    taglineFontSize: Number(e.target.value)
                  })
                }
              >
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
                <option value="13">13</option>
                <option value="14">14</option>
                <option value="15">15</option>
                <option value="16">16</option>
                <option value="17">17</option>
                <option value="18">18</option>
                <option value="19">19</option>
                <option value="20">20</option>
                <option value="21">21</option>
                <option value="22">22</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Text font size
              <select
                className="mt-1 border rounded px-2 py-1"
                value={String(labelDesign.textFontSize ?? 9)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    textFontSize: Number(e.target.value)
                  })
                }
              >
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
                <option value="13">13</option>
                <option value="14">14</option>
                <option value="15">15</option>
                <option value="16">16</option>
                <option value="17">17</option>
                <option value="18">18</option>
                <option value="19">19</option>
                <option value="20">20</option>
                <option value="21">21</option>
                <option value="22">22</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Text darkness (%)
              <select
                className="mt-1 border rounded px-2 py-1"
                value={String(labelDesign.textDarkness ?? 100)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    textDarkness: Number(e.target.value)
                  })
                }
              >
                <option value="100">100 (Black)</option>
                <option value="90">90</option>
                <option value="80">80</option>
                <option value="70">70</option>
                <option value="60">60</option>
                <option value="50">50</option>
                <option value="40">40 (Light)</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Text weight
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={labelDesign.textBold ?? false}
                  onChange={e =>
                    setLabelDesign({
                      ...labelDesign,
                      textBold: e.target.checked
                    })
                  }
                  aria-label="Use bold weight for label body text"
                />
                <span>Bold</span>
              </div>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Line spacing
              <input
                type="range"
                min="0.9"
                max="4"
                step="0.05"
                className="mt-2"
                value={String(labelDesign.lineHeight ?? 1.25)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    lineHeight: Number(e.target.value)
                  })
                }
              />
              <span className="text-xs text-gray-500 mt-1">
                {(labelDesign.lineHeight ?? 1.25).toFixed(2)}x
              </span>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Spacing (mm / in)
              <input
                type="range"
                min="0.5"
                max="6"
                step="0.5"
                className="mt-2"
                value={String(labelDesign.contentGapMm ?? 0.5)}
                onChange={e =>
                  setLabelDesign({
                    ...labelDesign,
                    contentGapMm: Number(e.target.value)
                  })
                }
              />
              <span className="text-xs text-gray-500 mt-1">
                {(labelDesign.contentGapMm ?? 0.5).toFixed(1)} mm (~{' '}
                {formatInches(labelDesign.contentGapMm ?? 0.5)} in)
              </span>
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col text-sm text-gray-700">
              Line 1
              <input
                className="mt-1 border rounded px-2 py-1"
                maxLength={120}
                value={labelDesign.freeLines?.[0] || ''}
                onChange={e => handleFreeLineChange(0, e.target.value)}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Line 2
              <input
                className="mt-1 border rounded px-2 py-1"
                maxLength={120}
                value={labelDesign.freeLines?.[1] || ''}
                onChange={e => handleFreeLineChange(1, e.target.value)}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Line 3
              <input
                className="mt-1 border rounded px-2 py-1"
                maxLength={120}
                value={labelDesign.freeLines?.[2] || ''}
                onChange={e => handleFreeLineChange(2, e.target.value)}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500">
            If MRP is set in product info, line 3 is replaced with the MRP on print.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex flex-col text-sm text-gray-700">
              Logo (optional)
              <input
                type="file"
                accept="image/*"
                className="mt-1 text-sm"
                onChange={handleLogoUpload}
                disabled={logoUploading || labelDesignSaving}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Logo width (mm)
              <input
                type="number"
                min={6}
                max={Number(previewMaxLogoWidthMm.toFixed(1))}
                step={0.5}
                className="mt-1 border rounded px-2 py-1 w-24"
                value={labelDesign.logoWidthMm ?? 10}
                onChange={e =>
                  setLabelDesign(prev => ({
                    ...prev,
                    logoWidthMm: e.target.value ? Number(e.target.value) : 10
                  }))
                }
              />
              <span className="mt-1 text-xs text-gray-500">
                ~ {formatInches(labelDesign.logoWidthMm ?? 10)} in
              </span>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Logo height (mm)
              <input
                type="number"
                min={6}
                max={Number(previewMaxLogoHeightMm.toFixed(1))}
                step={0.5}
                className="mt-1 border rounded px-2 py-1 w-24"
                value={labelDesign.logoHeightMm ?? 10}
                onChange={e =>
                  setLabelDesign(prev => ({
                    ...prev,
                    logoHeightMm: e.target.value ? Number(e.target.value) : 10
                  }))
                }
              />
              <span className="mt-1 text-xs text-gray-500">
                ~ {formatInches(labelDesign.logoHeightMm ?? 10)} in
              </span>
            </label>
            {logoUploading && <span className="text-xs text-gray-500">Uploading...</span>}
            {labelDesign.logoUrl && (
              <div className="h-12 w-12 border border-gray-200 rounded bg-white flex items-center justify-center overflow-hidden">
                <img src={labelDesign.logoUrl} alt="Label logo preview" className="max-h-full max-w-full" />
              </div>
            )}
            {(labelDesign.logoUrl || labelDesign.logoPath) && (
              <Button variant="outline" size="sm" onClick={handleClearLogo}>
                Remove logo
              </Button>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Logo and text are optional. Save design to apply it to this batch.
          </p>
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Live preview</span>
              <span>{thermalOptions.size} label</span>
            </div>
            <div className="mt-2 flex justify-center">
              <div
                className="bg-white border border-gray-200 shadow-sm"
                style={{ width: previewWidthPx, aspectRatio: previewRatio }}
              >
                <div className={`flex h-full w-full ${previewLayoutClass}`}>
                  {previewIsVertical ? (
                    <div className="flex h-full w-full flex-col" style={{ padding: previewPaddingPx }}>
                      <div className="flex flex-col items-center justify-center">
                        {previewBranding && (
                          <div
                            className="text-center"
                            style={{
                              fontSize: previewBrandingSize,
                              lineHeight: previewLineHeight,
                              marginBottom: previewQrTextGapPx,
                              color: previewQrLabelColor,
                              fontWeight: previewQrTextBold ? 600 : 400
                            }}
                          >
                            {previewBranding}
                          </div>
                        )}
                        <div
                          className="flex items-center justify-center"
                          style={{ width: previewQrPx, height: previewQrPx }}
                        >
                          <div
                            className="h-full w-full text-[8px] font-semibold flex items-center justify-center"
                            style={{ backgroundColor: previewQrFill, color: previewQrTextColor }}
                          >
                            QR
                          </div>
                        </div>
                        {previewCaption && (
                          <div
                            className="text-center"
                            style={{
                              fontSize: previewCaptionSize,
                              lineHeight: previewLineHeight,
                              marginTop: previewQrTextGapPx,
                              color: previewQrLabelColor,
                              fontWeight: previewQrTextBold ? 600 : 400
                            }}
                          >
                            {previewCaption}
                          </div>
                        )}
                      </div>
                      <div
                        className={`mt-3 flex flex-col ${previewAlignClass}`}
                        style={{ gap: Math.max(2, Math.round(previewGapPx * 0.6)) }}
                      >
                        {showLogoPreview && (
                          labelDesign.logoUrl ? (
                            <img
                              src={labelDesign.logoUrl}
                              alt="Logo preview"
                              className="object-contain self-center"
                              style={{ width: previewLogoWidthPx, height: previewLogoHeightPx }}
                            />
                          ) : (
                            <div
                              className="border border-dashed border-gray-300 text-[8px] text-gray-500 flex items-center justify-center self-center"
                              style={{ width: previewLogoWidthPx, height: previewLogoHeightPx }}
                            >
                              Logo
                            </div>
                          )
                        )}
                        <div className={`flex flex-col ${previewAlignClass}`}>
                          {previewTextLines.map((line, index) => (
                            <div
                              key={`${line.text}-${index}`}
                              className={line.weight ? 'font-semibold' : 'font-normal'}
                              style={{
                                fontSize: Math.max(6, Math.round(line.size)),
                                lineHeight: previewLineHeight,
                                color: line.color ?? previewTextColor,
                                fontWeight: line.weight ?? 400
                              }}
                            >
                              {line.text}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className="flex h-full items-center justify-center"
                        style={{ width: previewQrZoneWidthPx }}
                      >
                        <div
                          className="flex flex-col items-center justify-center"
                          style={{ width: previewQrZoneInnerWidthPx, height: previewZoneInnerHeightPx }}
                        >
                          {previewQrTextLayout === 'side' ? (
                            <div
                              className="flex items-center justify-center"
                              style={{ gap: previewSideTextGapPx }}
                            >
                              {previewQrOnLeft && (previewBranding || previewCaption) && (
                                <div
                                  className="flex items-center justify-center"
                                  style={{ width: previewSideColumnWidth, height: previewQrPx }}
                                >
                                  <div
                                    className="flex flex-col items-center justify-center"
                                    style={{
                                      transform:
                                        previewQrTextOrientation === 'bottom_to_top'
                                          ? 'rotate(-90deg)'
                                          : 'rotate(90deg)',
                                      transformOrigin: 'center',
                                      fontSize: previewQrLabelSize,
                                      lineHeight: previewLineHeight,
                                      gap: previewSideBlockGapPx,
                                      whiteSpace: 'nowrap',
                                      color: previewQrLabelColor,
                                      fontWeight: previewQrTextBold ? 600 : 400
                                    }}
                                  >
                                    {previewBranding && <span>{previewBranding}</span>}
                                    {previewCaption && <span>{previewCaption}</span>}
                                  </div>
                                </div>
                              )}
                              <div
                                className="flex items-center justify-center"
                                style={{ width: previewQrPx, height: previewQrPx }}
                              >
                                <div
                                  className="h-full w-full text-[8px] font-semibold flex items-center justify-center"
                                  style={{ backgroundColor: previewQrFill, color: previewQrTextColor }}
                                >
                                  QR
                                </div>
                              </div>
                              {!previewQrOnLeft && (previewBranding || previewCaption) && (
                                <div
                                  className="flex items-center justify-center"
                                  style={{ width: previewSideColumnWidth, height: previewQrPx }}
                                >
                                  <div
                                    className="flex flex-col items-center justify-center"
                                    style={{
                                      transform:
                                        previewQrTextOrientation === 'bottom_to_top'
                                          ? 'rotate(-90deg)'
                                          : 'rotate(90deg)',
                                      transformOrigin: 'center',
                                      fontSize: previewQrLabelSize,
                                      lineHeight: previewLineHeight,
                                      gap: previewSideBlockGapPx,
                                      whiteSpace: 'nowrap',
                                      color: previewQrLabelColor,
                                      fontWeight: previewQrTextBold ? 600 : 400
                                    }}
                                  >
                                    {previewBranding && <span>{previewBranding}</span>}
                                    {previewCaption && <span>{previewCaption}</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              {previewBranding && (
                                <div
                                  className="text-center"
                                  style={{
                                    fontSize: previewBrandingSize,
                                    lineHeight: previewLineHeight,
                                    marginBottom: previewQrTextGapPx,
                                    color: previewQrLabelColor,
                                    fontWeight: previewQrTextBold ? 600 : 400
                                  }}
                                >
                                  {previewBranding}
                                </div>
                              )}
                              <div
                                className="flex items-center justify-center"
                                style={{ width: previewQrPx, height: previewQrPx }}
                              >
                                <div
                                  className="h-full w-full text-[8px] font-semibold flex items-center justify-center"
                                  style={{ backgroundColor: previewQrFill, color: previewQrTextColor }}
                                >
                                  QR
                                </div>
                              </div>
                              {previewCaption && (
                                <div
                                  className="text-center"
                                  style={{
                                    fontSize: previewCaptionSize,
                                    lineHeight: previewLineHeight,
                                    marginTop: previewQrTextGapPx,
                                    color: previewQrLabelColor,
                                    fontWeight: previewQrTextBold ? 600 : 400
                                  }}
                                >
                                  {previewCaption}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className="flex h-full items-start justify-center"
                        style={{ width: previewTextZoneWidthPx }}
                      >
                        <div
                          className={`flex w-full flex-col ${previewAlignClass} ${previewTextJustifyClass}`}
                          style={{
                            width: previewTextZoneInnerWidthPx,
                            height: previewZoneInnerHeightPx,
                            paddingTop: 0
                          }}
                        >
                          {showLogoPreview && (
                            labelDesign.logoUrl ? (
                              <img
                                src={labelDesign.logoUrl}
                                alt="Logo preview"
                                className="object-contain self-center"
                                style={{ width: previewLogoWidthPx, height: previewLogoHeightPx }}
                              />
                            ) : (
                              <div
                                className="border border-dashed border-gray-300 text-[8px] text-gray-500 flex items-center justify-center self-center"
                                style={{ width: previewLogoWidthPx, height: previewLogoHeightPx }}
                              >
                                Logo
                              </div>
                            )
                          )}
                          <div className={`${previewTextGapClass} flex flex-col ${previewAlignClass}`}>
                            {previewTextLines.map((line, index) => (
                              <div
                                key={`${line.text}-${index}`}
                                className={line.weight ? 'font-semibold' : 'font-normal'}
                                style={{
                                  fontSize: Math.max(6, Math.round(line.size)),
                                  lineHeight: previewLineHeight,
                                  color: line.color ?? previewTextColor,
                                  fontWeight: line.weight ?? 400
                                }}
                              >
                                {line.text}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t pt-4 mt-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-800">Thermal Label (single)</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col text-sm text-gray-700">
              Size (in / mm)
              <select
                className="mt-1 border rounded px-2 py-1"
                value={activeLabelSize}
                onChange={e => applyLabelSize(e.target.value as ThermalLabelSize)}
              >
                <option value="2x1">2.0 x 1.0 in (50.8 x 25.4 mm)</option>
                <option value="2x1.5">2.0 x 1.5 in (50.8 x 38.1 mm)</option>
                <option value="2x2">2.0 x 2.0 in (50.8 x 50.8 mm)</option>
                <option value="50x30mm">50 x 30 mm (1.97 x 1.18 in)</option>
                <option value="50x34mm">50 x 34 mm (1.97 x 1.34 in)</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              DPI
              <select
                className="mt-1 border rounded px-2 py-1"
                value={thermalOptions.dpi}
                onChange={e => setThermalOptions({ ...thermalOptions, dpi: Number(e.target.value) as 203 | 300 | 600 })}
              >
                <option value={203}>203</option>
                <option value={300}>300</option>
                <option value={600}>600</option>
              </select>
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              Margin (mm)
              <input
                type="number"
                min={0.5}
                max={6}
                step={0.5}
                className="mt-1 border rounded px-2 py-1"
                value={thermalOptions.marginMm ?? 2.5}
                onChange={e =>
                  setThermalOptions({
                    ...thermalOptions,
                    marginMm: e.target.value ? Number(e.target.value) : 2.5
                  })
                }
              />
              <span className="mt-1 text-xs text-gray-500">
                ~ {formatInches(thermalOptions.marginMm ?? 2.5)} in
              </span>
            </label>
            {!thermalOptions.fullBatch && (
              <label className="flex flex-col text-sm text-gray-700">
                Serial number (optional)
                <input
                  type="number"
                  className="mt-1 border rounded px-2 py-1"
                  value={thermalOptions.serialNumber ?? ''}
                  placeholder="Defaults to first product"
                  onChange={e =>
                    setThermalOptions({
                      ...thermalOptions,
                      serialNumber: e.target.value ? Number(e.target.value) : undefined
                    })
                  }
                />
              </label>
            )}
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={thermalOptions.includePdf}
                onChange={e => setThermalOptions({ ...thermalOptions, includePdf: e.target.checked })}
                aria-label="Also generate PDF for thermal labels"
              />
              <span>Also generate PDF</span>
            </label>
            <label className="flex items-center space-x-2 text-sm text-gray-700 sm:col-span-2">
              <input
                type="checkbox"
                checked={thermalOptions.fullBatch}
                onChange={e => setThermalOptions({ ...thermalOptions, fullBatch: e.target.checked })}
                aria-label="Generate thermal labels for entire batch"
              />
              <span>Generate for entire batch</span>
            </label>
            <label className="flex items-center space-x-2 text-sm text-gray-700 sm:col-span-2">
              <input
                type="checkbox"
                checked={thermalOptions.includeInner}
                onChange={e => setThermalOptions({ ...thermalOptions, includeInner: e.target.checked })}
                disabled={!innerEnabled}
                aria-label="Include inner QR label in thermal output"
              />
              <span>Include inner QR label</span>
              {!innerEnabled && (
                <span className="text-xs text-gray-400">(inner QR disabled for this batch)</span>
              )}
            </label>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
            <Button onClick={handleGenerateThermalLabel} disabled={generatingPack} className="w-full sm:w-auto">
              {generatingPack ? 'Generating...' : 'Generate Thermal Label'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );

  const handleDownloadQRCodes = async () => {
    if (!batchId) return;
    
    try {
      setDownloading(true);
      setError(null);
      await productService.downloadBatchQRCodes(batchId);
      setSuccess('QR codes downloaded successfully!');
    } catch (err: any) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to download QR codes');
    } finally {
      setTimeout(() => setDownloading(false), 2000);
    }
  };

  const handleDownloadVerifyUrls = async () => {
    if (!batchId) return;
    try {
      setDownloadingUrls(true);
      setError(null);
      const allProducts = await productService.getProductsByBatch(batchId, MAX_LABELS);
      const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;
      const header = innerEnabled ? 'serialNumber,outerUrl,innerUrl' : 'serialNumber,outerUrl';
      const rows = allProducts.map(p => {
        const outerUrl = `${baseUrl}/vf/${p.outerQrToken}`;
        if (!innerEnabled || !p.innerQrToken) {
          return `${p.serialNumber},"${outerUrl}"`;
        }
        const innerUrl = `${baseUrl}/vf/${p.innerQrToken}`;
        return `${p.serialNumber},"${outerUrl}","${innerUrl}"`;
      });
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const label = batch?.productName || batchId || 'batch';
      link.download = `${label}-verify-urls.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      setSuccess(innerEnabled ? 'Verification URLs downloaded (outer and inner).' : 'Verification URLs downloaded (outer only).');
    } catch (err: any) {
      console.error('Verify URL download error:', err);
      setError(err.message || 'Failed to download verification URLs');
    } finally {
      setTimeout(() => setDownloadingUrls(false), 2000);
    }
  };

  const handleDownloadCaseQRs = async () => {
    if (!batchId) return;
    
    try {
      setDownloadingCases(true);
      setError(null);
      await productService.downloadHierarchicalQRs(batchId, 'case');
      setSuccess('Case QR codes downloaded successfully!');
    } catch (err: any) {
      console.error('Case QR download error:', err);
      setError(err.message || 'Failed to download case QR codes. Please ensure packages have been created.');
    } finally {
      setTimeout(() => setDownloadingCases(false), 2000);
    }
  };

  const handleDownloadPalletQRs = async () => {
    if (!batchId) return;
    
    try {
      setDownloadingPallets(true);
      setError(null);
      await productService.downloadHierarchicalQRs(batchId, 'pallet');
      setSuccess('Pallet QR codes downloaded successfully!');
    } catch (err: any) {
      console.error('Pallet QR download error:', err);
      setError(err.message || 'Failed to download pallet QR codes. Please ensure packages have been created.');
    } finally {
      setTimeout(() => setDownloadingPallets(false), 2000);
    }
  };

  const handleDeleteBatch = async () => {
    if (!batchId) return;

    try {
      setDeleteError(null);
      setDeletingBatch(true);
      const result = await productService.deleteBatch(batchId);

      if (result.success) {
        setSuccess(result.message || 'Batch deleted and credits restored.');
        setShowDeleteModal(false);
        setTimeout(() => {
          navigate('/products');
        }, 1200);
      } else {
        setDeleteError(result.error || 'Failed to delete batch');
      }
    } catch (err: any) {
      console.error('Batch deletion error:', err);
      setDeleteError(err.response?.data?.error || err.message || 'Failed to delete batch');
    } finally {
      setDeletingBatch(false);
    }
  };

  const handleCreatePackages = async () => {
    if (!batchId) return;
    
    try {
      setCreatingPackages(true);
      setError(null);
      const result = await packagingService.createPackages(batchId);
      
      if (result.success) {
        setSuccess('Packages created successfully! You can now download case and pallet QR codes.');
        // Reload batch details to refresh the UI
        setTimeout(() => {
          loadBatchDetails();
        }, 1000);
      } else {
        setError(result.error || 'Failed to create packages');
      }
    } catch (err: any) {
      console.error('Package creation error:', err);
      setError('Failed to create packages');
    } finally {
      setCreatingPackages(false);
    }
  };

  const handleActivatePackage = async (packageId: string) => {
    try {
      setError(null);
      const result = await packagingService.activatePackage(packageId);
      
      if (result.success) {
        setSuccess(`Package activated successfully! ${result.results.newlyActivated} products activated.`);
        // Reload packages to refresh activation status
        loadBatchDetails();
      } else {
        setError(result.error || 'Failed to activate package');
      }
    } catch (err: any) {
      console.error('Package activation error:', err);
      setError('Failed to activate package');
    }
  };

  const handleBulkActivatePackages = async () => {
    if (selectedPackages.length === 0) return;
    
    try {
      setActivatingPackages(true);
      setError(null);
      
      const result = await packagingService.bulkActivatePackages(selectedPackages);
      
      if (result.success && result.results) {
        setSuccess(`Bulk activation completed! ${result.results.totalProductsActivated} products activated across ${result.results.successful} packages.`);
        setShowActivationModal(false);
        setSelectedPackages([]);
        // Reload packages to refresh activation status
        loadBatchDetails();
      } else {
        setError(result.error || 'Failed to activate packages');
      }
    } catch (err: any) {
      console.error('Bulk package activation error:', err);
      setError('Failed to activate packages');
    } finally {
      setActivatingPackages(false);
    }
  };

  const handleSelectPackage = (packageId: string, selected: boolean) => {
    if (selected) {
      setSelectedPackages([...selectedPackages, packageId]);
    } else {
      setSelectedPackages(selectedPackages.filter(id => id !== packageId));
    }
  };

  const handleSelectAllPackages = (selected: boolean) => {
    if (selected) {
      const activatablePackages = packages.filter(pkg => pkg.canActivate).map(pkg => pkg.id);
      setSelectedPackages(activatablePackages);
    } else {
      setSelectedPackages([]);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const productStatusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: products.length },
      { value: 'active', label: 'Active', count: products.filter((product) => product.status === 'active').length },
      {
        value: 'consumed',
        label: 'Consumed',
        count: products.filter((product) => product.status === 'consumed').length,
      },
      { value: 'expired', label: 'Expired', count: products.filter((product) => product.status === 'expired').length },
      { value: 'revoked', label: 'Revoked', count: products.filter((product) => product.status === 'revoked').length },
    ],
    [products]
  );
  const filteredProducts = useMemo(() => {
    const normalizedQuery = productSearchQuery.trim().toLowerCase();
    const filtered = products.filter((product) => {
      const matchesStatus = productStatusFilter === 'all' || product.status === productStatusFilter;
      const matchesQuery =
        !normalizedQuery ||
        String(product.serialNumber).includes(normalizedQuery) ||
        product.status.toLowerCase().includes(normalizedQuery) ||
        product.activationStatus.toLowerCase().includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });

    return filtered.sort((a, b) => {
      if (productSort === 'serial-desc') return b.serialNumber - a.serialNumber;
      if (productSort === 'created-desc') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (productSort === 'created-asc') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return a.serialNumber - b.serialNumber;
    });
  }, [productSearchQuery, productSort, productStatusFilter, products]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading batch details...</p>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Batch not found</p>
          <Button onClick={() => navigate('/products/create-batch')} className="mt-4">
            Back to Batch Management
          </Button>
        </div>
      </div>
    );
  }

  const productCount = batch.serialEnd - batch.serialStart + 1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <PageHeader
            title="Batch Details"
            description={formatBatchName(batch.productName, batch.createdAt, batch.batchTitle)}
            actions={
              <Button variant="outline" onClick={() => navigate('/products/create-batch')}>
                Back to Batch Management
              </Button>
            }
          />
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

        {/* Batch Info Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {formatBatchName(batch.productName, batch.createdAt, batch.batchTitle)}
              </h2>
              <p className="text-sm text-gray-600">Batch ID: {batch.id}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/products/batch/${batchId}/manage`)}
                className="w-full sm:w-auto"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Manage Product Info
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPrintPackModal(true)}
                className="w-full sm:w-auto flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v4H4zM4 10h10v4H4zM4 16h16v4H4z" />
                </svg>
                Print Pack (A4/Letter)
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowThermalLabelModal(true)}
                className="w-full sm:w-auto flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v4H4zM4 10h10v4H4zM4 16h16v4H4z" />
                </svg>
                Thermal Labels
              </Button>
              
              <Button
                onClick={handleDownloadQRCodes}
                disabled={downloading}
                className="w-full sm:w-auto flex items-center gap-2"
              >
                {downloading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download All QR Codes
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/packaging/config/${batchId}`)}
                className="w-full sm:w-auto flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10M4 18h7" />
                </svg>
                Configure Packaging
              </Button>

              <Button
                onClick={handleDownloadVerifyUrls}
                disabled={downloadingUrls}
                className="w-full sm:w-auto flex items-center gap-2"
                variant="outline"
              >
                {downloadingUrls ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    Preparing URLs...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-2 2a4 4 0 01-5.656-5.656l1.172-1.172M10.172 13.828a4 4 0 010-5.656l2-2a4 4 0 015.656 5.656l-1.172 1.172" />
                    </svg>
                    Download Verify URLs
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={openConfidentialSection}
                className="w-full sm:w-auto flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                </svg>
                Confidential Info
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/api-access')}
                className="w-full sm:w-auto flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2h-1V9a5 5 0 10-10 0v2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                API Access
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setDeleteError(null);
                  setShowDeleteModal(true);
                }}
                className="w-full sm:w-auto flex items-center gap-2"
              >
                Delete Batch
              </Button>
            </div>
          </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">Total Products</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{productCount.toLocaleString()}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600 font-medium">Serial Range</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {batch.serialStart} - {batch.serialEnd}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-600 font-medium">Created</p>
              <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(batch.createdAt)}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm text-orange-600 font-medium">Status</p>
              <div className="mt-1">
                <StatusBadge tone="active" size="sm">Active</StatusBadge>
              </div>
            </div>
          </div>
        </div>

        {/* Confidential Information */}
        <div id="confidential-info-section" className="bg-white rounded-lg shadow p-6 mb-6 scroll-mt-24">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Confidential Information</h3>
              <p className="text-sm text-gray-600 mt-1">
                Publish dealer and retailer-only notes for this batch. Latest release is used in QR access.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <StatusBadge tone={confidentialSubscription?.active ? 'active' : 'disabled'} size="sm">
                  {confidentialSubscription?.active ? 'Premium Active' : 'Premium Required'}
                </StatusBadge>
                {confidentialSubscription?.validUntil ? (
                  <span className="text-xs text-gray-500">
                    Valid until {formatDate(confidentialSubscription.validUntil)}
                  </span>
                ) : null}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (batchId) {
                  void loadConfidentialData(batchId);
                }
              }}
              disabled={confidentialLoading}
            >
              {confidentialLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {confidentialError ? (
            <Alert variant="error" className="mb-4">
              {confidentialError}
            </Alert>
          ) : null}

          {!confidentialSubscription?.active ? (
            <Alert variant="info" className="mb-4">
              Premium subscription is required to publish confidential releases. Unlock it from API Access.
              <div className="mt-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/api-access')}
                >
                  Open API Access
                </Button>
              </div>
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Retailer Payload (Required)</h4>
              <input
                type="text"
                value={confidentialForm.retailerDiscount}
                onChange={(event) =>
                  setConfidentialForm((current) => ({
                    ...current,
                    retailerDiscount: event.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Discount (example: 8% off)"
              />
              <input
                type="text"
                value={confidentialForm.retailerPromoCode}
                onChange={(event) =>
                  setConfidentialForm((current) => ({
                    ...current,
                    retailerPromoCode: event.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Promo code"
              />
              <textarea
                value={confidentialForm.retailerInstruction}
                onChange={(event) =>
                  setConfidentialForm((current) => ({
                    ...current,
                    retailerInstruction: event.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
                rows={3}
                placeholder="Special retailer instruction"
              />
            </div>
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Dealer Payload (Optional)</h4>
              <input
                type="text"
                value={confidentialForm.dealerDiscount}
                onChange={(event) =>
                  setConfidentialForm((current) => ({
                    ...current,
                    dealerDiscount: event.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Discount"
              />
              <input
                type="text"
                value={confidentialForm.dealerPromoCode}
                onChange={(event) =>
                  setConfidentialForm((current) => ({
                    ...current,
                    dealerPromoCode: event.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Promo code"
              />
              <textarea
                value={confidentialForm.dealerInstruction}
                onChange={(event) =>
                  setConfidentialForm((current) => ({
                    ...current,
                    dealerInstruction: event.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
                rows={3}
                placeholder="Special dealer instruction"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
            <label className="flex flex-col text-sm text-gray-700">
              <span className="font-medium mb-1">Valid Until (optional)</span>
              <input
                type="datetime-local"
                value={confidentialForm.validUntil}
                onChange={(event) =>
                  setConfidentialForm((current) => ({
                    ...current,
                    validUntil: event.target.value,
                  }))
                }
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700 lg:col-span-2">
              <span className="font-medium mb-1">Internal Notes (optional)</span>
              <input
                type="text"
                value={confidentialForm.notes}
                onChange={(event) =>
                  setConfidentialForm((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
                className="border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Release notes for internal team"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Button
              onClick={handleCreateConfidentialRelease}
              disabled={confidentialSaving || !confidentialSubscription?.active}
            >
              {confidentialSaving ? 'Publishing...' : 'Publish Confidential Release'}
            </Button>
            {confidentialLatest ? (
              <span className="text-sm text-gray-600">
                Latest version: v{confidentialLatest.version} ({confidentialLatest.status})
              </span>
            ) : (
              <span className="text-sm text-gray-500">No release published yet.</span>
            )}
          </div>

          <div className="space-y-3">
            {confidentialLoading ? (
              <p className="text-sm text-gray-500">Loading releases...</p>
            ) : confidentialReleases.length === 0 ? (
              <p className="text-sm text-gray-500">No confidential release history found for this batch.</p>
            ) : (
              confidentialReleases.map((release) => (
                <div key={release.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">v{release.version}</span>
                      <StatusBadge tone={release.status === 'active' ? 'active' : 'disabled'} size="sm">
                        {release.status}
                      </StatusBadge>
                    </div>
                    <div className="text-xs text-gray-500">
                      Issued {formatDate(release.issuedAt)} · Valid until {formatDate(release.validUntil)}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 rounded p-3">
                      <p className="font-semibold text-gray-800 mb-1">Retailer</p>
                      <p className="text-gray-700">Discount: {release.retailerPayload?.discount || '-'}</p>
                      <p className="text-gray-700">Promo: {release.retailerPayload?.promoCode || '-'}</p>
                      <p className="text-gray-700">Instruction: {release.retailerPayload?.instruction || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <p className="font-semibold text-gray-800 mb-1">Dealer</p>
                      <p className="text-gray-700">Discount: {release.dealerPayload?.discount || '-'}</p>
                      <p className="text-gray-700">Promo: {release.dealerPayload?.promoCode || '-'}</p>
                      <p className="text-gray-700">Instruction: {release.dealerPayload?.instruction || '-'}</p>
                    </div>
                  </div>
                  {release.notes ? (
                    <p className="text-sm text-gray-600 mt-3">
                      <span className="font-medium">Notes:</span> {release.notes}
                    </p>
                  ) : null}
                  {release.status === 'active' ? (
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevokeConfidentialRelease(release.id)}
                        disabled={confidentialRevokingId === release.id}
                      >
                        {confidentialRevokingId === release.id ? 'Revoking...' : 'Revoke'}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Packaging Configuration */}
        {packagingConfig ? (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Packaging Configuration</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/packaging/config/${batchId}`)}
              >
                Edit Config
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium">Units per Case</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{packagingConfig.unitsPerCase}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-600 font-medium">Cases per Pallet</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{packagingConfig.casesPerPallet}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">Total Cases</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {Math.ceil(productCount / packagingConfig.unitsPerCase)}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-sm text-orange-600 font-medium">Total Pallets</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {Math.ceil(productCount / (packagingConfig.unitsPerCase * packagingConfig.casesPerPallet))}
                </p>
              </div>
            </div>

            {/* Create Packages Button */}
            <div className="mb-4">
              <Button
                onClick={handleCreatePackages}
                disabled={creatingPackages}
                className="flex items-center gap-2"
              >
                {creatingPackages ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating Packages...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Create Packages
                  </>
                )}
              </Button>
              <p className="text-sm text-gray-600 mt-2">
                Create physical packages (cases and pallets) to enable QR code downloads
              </p>
            </div>

            {/* Package QR Download Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={handleDownloadQRCodes}
                disabled={downloading}
                className="flex items-center gap-2"
              >
                {downloading ? 'Downloading...' : 'Unit QRs'}
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadCaseQRs}
                disabled={downloadingCases}
                className="flex items-center gap-2"
              >
                {downloadingCases ? 'Downloading...' : 'Case QRs'}
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadPalletQRs}
                disabled={downloadingPallets}
                className="flex items-center gap-2"
              >
                {downloadingPallets ? 'Downloading...' : 'Pallet QRs'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Packaging Configuration</h3>
                <p className="text-sm text-gray-600 mt-1">
                  No packaging set up yet. Configure cases and pallets to enable bulk shipping and QR downloads.
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => navigate(`/packaging/config/${batchId}`)}
                className="w-full sm:w-auto"
              >
                Configure Packaging
              </Button>
            </div>
          </div>
        )}

        {/* Packages List with activation status */}
        {packages.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Packages</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowActivationModal(true)}
              >
                Bulk Activate
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {packages.map((pkg) => {
                const progressPercent = pkg.totalProducts > 0
                  ? (pkg.activatedProducts / pkg.totalProducts) * 100
                  : 0;
                return (
                  <EntityCard
                    key={pkg.id}
                    title={`${pkg.packageType} Package`}
                    subtitle={`${pkg.id.substring(0, 8)}...`}
                    badge={
                      <StatusBadge tone={pkg.packageType === 'PALLET' ? 'anchored' : 'active'} size="sm">
                        {pkg.packageType}
                      </StatusBadge>
                    }
                    meta={[
                      { label: 'Activated', value: `${pkg.activatedProducts}/${pkg.totalProducts}` },
                      { label: 'Remaining', value: `${pkg.totalProducts - pkg.activatedProducts}` },
                    ]}
                    footer={
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <p className="text-sm text-gray-600">
                          {pkg.totalProducts - pkg.activatedProducts} products remaining
                        </p>
                        <Button
                          size="sm"
                          onClick={() => handleActivatePackage(pkg.id)}
                          disabled={!pkg.canActivate}
                        >
                          {pkg.canActivate ? 'Activate' : 'Completed'}
                        </Button>
                      </div>
                    }
                  >
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-green-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </EntityCard>
                );
              })}
            </div>
          </div>
        )}

        {/* Product Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 sm:px-6 pt-4">
            <FilterBar
              searchValue={productSearchQuery}
              onSearchChange={setProductSearchQuery}
              searchPlaceholder="Search serial number or status..."
              statusOptions={productStatusOptions}
              selectedStatus={productStatusFilter}
              onStatusChange={setProductStatusFilter}
              sortOptions={[
                { value: 'serial-asc', label: 'Sort: Serial Low to High' },
                { value: 'serial-desc', label: 'Sort: Serial High to Low' },
                { value: 'created-desc', label: 'Sort: Latest Created' },
                { value: 'created-asc', label: 'Sort: Oldest Created' },
              ]}
              selectedSort={productSort}
              onSortChange={(value) =>
                setProductSort(value as 'serial-asc' | 'serial-desc' | 'created-desc' | 'created-asc')
              }
              className="mb-4"
            />
          </div>
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Products</h3>
            <p className="text-sm text-gray-600">Showing {filteredProducts.length} products in the batch</p>
          </div>
          
          {filteredProducts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" aria-label="Batch products">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial #</th>
                    <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activation</th>
                    <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.map((product) => {
                    const productStatusBadge = mapStatusToBadge(product.status);
                    const activationStatusBadge = mapStatusToBadge(product.activationStatus);
                    return (
                      <tr key={product.id}>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.serialNumber}</td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          <StatusBadge tone={productStatusBadge.tone} size="sm">
                            {productStatusBadge.label}
                          </StatusBadge>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          <StatusBadge tone={activationStatusBadge.tone} size="sm">
                            {activationStatusBadge.label}
                          </StatusBadge>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(product.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-gray-500">
              No products found for the selected filters
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-col sm:flex-row sm:justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/products')}
            className="w-full sm:w-auto"
          >
            View All Products
          </Button>
          <Button
            onClick={() => navigate('/products/create-batch')}
            className="w-full sm:w-auto"
          >
            Create Another Batch
          </Button>
        </div>
      {printPackModal}
      {thermalLabelModal}
      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteError(null);
          }}
          title="Delete Batch"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Deleting this batch removes all of its products, QR codes, and associated metadata, while restoring the credits you spent. This action is only available within 24 hours of creation and cannot be undone.
            </p>
            {deleteError && (
              <Alert variant="error">{deleteError}</Alert>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteError(null);
                }}
                disabled={deletingBatch}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteBatch}
                isLoading={deletingBatch}
              >
                Delete batch
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Activation Modal */}
      {showActivationModal && (
        <Modal
          isOpen={showActivationModal}
          onClose={() => {
            setShowActivationModal(false);
            setSelectedPackages([]);
          }}
          title="Bulk Package Activation"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Select packages to activate all products within them at once. This is much faster than activating individual products.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h4 className="text-sm font-medium text-gray-900">Select Packages</h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSelectAllPackages(true)}
                    className="text-sm text-blue-600 hover:text-blue-900"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => handleSelectAllPackages(false)}
                    className="text-sm text-blue-600 hover:text-blue-900"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto space-y-2">
                {packages.filter(pkg => pkg.canActivate).map((pkg) => (
                  <div key={pkg.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedPackages.includes(pkg.id)}
                        onChange={(e) => handleSelectPackage(pkg.id, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        aria-label={`Select package ${pkg.id} for activation`}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                            pkg.packageType === 'PALLET' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {pkg.packageType}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {pkg.id.substring(0, 8)}...
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {pkg.totalProducts - pkg.activatedProducts} products to activate
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{pkg.activatedProducts}/{pkg.totalProducts}</p>
                      <div className="w-full sm:w-16 bg-gray-200 rounded-full h-1 mt-1">
                        <div 
                          className="h-1 rounded-full bg-yellow-500"
                          style={{ width: `${(pkg.activatedProducts / pkg.totalProducts) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {packages.filter(pkg => pkg.canActivate).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>All packages are already fully activated</p>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowActivationModal(false);
                  setSelectedPackages([]);
                }}
                disabled={activatingPackages}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkActivatePackages}
                disabled={selectedPackages.length === 0 || activatingPackages}
                className="w-full sm:w-auto"
              >
                {activatingPackages ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Activating...
                  </>
                ) : (
                  `Activate ${selectedPackages.length} Package${selectedPackages.length !== 1 ? 's' : ''}`
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

